import { getStoreApiErrorMessage } from "../helper/storeApiErrorMessage"
import { FLOOR_DESIGN_LABEL } from "./organizationApiHelpers"

/** Feature credit fields on admin plan create/update (legacy top-level keys). */
export const PLAN_FEATURE_CREDIT_FIELDS = [
  "home_redesign",
  "text_to_image",
  "object_removal",
  "sketch_to_render",
  "free_generations",
  "furniture_finder",
  "design_transfer",
  "pattern_transfer",
  "decor_transfer",
  "furniture_removal",
  "zone_detection",
  "video_generation",
  "reference_style",
  "wall_design",
  "floor_design",
  "floor_generation",
  "enhance_image",
  "furniture_replace",
  "rearrange_furniture",
  "expert_design",
]

/** Features configurable in Allowed Features (image cost per generation). */
export const PLAN_GENERATION_FEATURES = PLAN_FEATURE_CREDIT_FIELDS.filter(
  (key) => key !== "free_generations"
)

export const PLAN_FEATURE_LABELS = {
  home_redesign: { en: "Room Redesign", ar: "إعادة تصميم الغرفة" },
  text_to_image: { en: "Text to Image", ar: "نص إلى صورة" },
  object_removal: { en: "Quick Edits", ar: "تعديلات سريعة" },
  sketch_to_render: { en: "Sketch to Render", ar: "رسم إلى صورة" },
  furniture_finder: { en: "Furniture Finder", ar: "مستكشف الأثاث" },
  design_transfer: { en: "Design Transfer", ar: "نقل التصميم" },
  pattern_transfer: { en: "Pattern Transfer", ar: "نقل النمط" },
  decor_transfer: { en: "Decor Transfer", ar: "نقل الديكور" },
  furniture_removal: { en: "Furniture Removal", ar: "إزالة الأثاث" },
  zone_detection: { en: "Zone Detection", ar: "اكتشاف المناطق" },
  video_generation: { en: "Video Generation", ar: "إنشاء فيديو" },
  reference_style: { en: "Reference Style", ar: "نمط المرجع" },
  wall_design: { en: "Wall Design", ar: "تصميم الجدار" },
  floor_design: { en: FLOOR_DESIGN_LABEL, ar: FLOOR_DESIGN_LABEL },
  floor_generation: { en: "Floor Generation", ar: "إنشاء الأرضية" },
  enhance_image: { en: "Enhance Image", ar: "تحسين الصورة" },
  furniture_replace: { en: "Furniture Replace", ar: "استبدال الأثاث" },
  rearrange_furniture: { en: "Rearrange Furniture", ar: "إعادة ترتيب الأثاث" },
  expert_design: { en: "Expert Design", ar: "تصميم الخبير" },
}

export function getPlanFeatureLabel(key, lang = "en") {
  const labels = PLAN_FEATURE_LABELS[key]
  if (!labels) return key
  return lang === "ar" ? labels.ar : labels.en
}

/**
 * Label for a feature row from GET /account/admin/plans/features/.
 * @param {{ key: string, labelEn?: string, labelAr?: string }} feature
 */
export function getFeatureLabelFromCatalog(feature, lang = "en") {
  if (!feature) return ""
  if (feature.key && PLAN_FEATURE_LABELS[feature.key]) {
    return getPlanFeatureLabel(feature.key, lang)
  }
  return lang === "ar"
    ? feature.labelAr || feature.labelEn || feature.key
    : feature.labelEn || feature.labelAr || feature.key
}

/** Not selectable in allowed_features (configured via top-level field). */
export const PLAN_TOP_LEVEL_FEATURE_KEYS = ["furniture_finder"]

function normalizeCatalogFeatureItem(item) {
  if (typeof item === "string") {
    const key = item.trim()
    if (!key) return null
    return {
      key,
      labelEn: getPlanFeatureLabel(key, "en"),
      labelAr: getPlanFeatureLabel(key, "ar"),
      costType: "images_per_generation",
    }
  }
  if (!item || typeof item !== "object") return null

  const key = String(
    item.key ??
      item.slug ??
      item.feature_key ??
      item.feature ??
      item.code ??
      item.id ??
      ""
  ).trim()
  if (!key) return null

  return {
    key,
    labelEn:
      item.label ||
      item.name ||
      item.title ||
      item.name_english ||
      getPlanFeatureLabel(key, "en"),
    labelAr:
      item.label_arabic ||
      item.name_arabic ||
      item.title_arabic ||
      item.nameArabic ||
      getPlanFeatureLabel(key, "ar"),
    costType: item.cost_type || "images_per_generation",
  }
}

/**
 * GET /account/admin/plans/features/ → full config for the create-plan form.
 */
export function parsePlanFeaturesConfigFromApi(data) {
  const result = data?.result ?? data
  const rawFeatures = result?.features
  const features = Array.isArray(rawFeatures)
    ? rawFeatures.map(normalizeCatalogFeatureItem).filter(Boolean)
    : []

  const perGenerationFeatureKeys = Array.isArray(
    result?.per_generation_feature_keys
  )
    ? result.per_generation_feature_keys.map(String)
    : features
        .filter((f) => f.costType === "images_per_generation")
        .map((f) => f.key)

  const includedUseFeatureKeys = Array.isArray(result?.included_use_feature_keys)
    ? result.included_use_feature_keys.map(String)
    : []

  if (features.length === 0) {
    const fallback = planFeaturesCatalogFallback()
    return {
      features: fallback,
      perGenerationFeatureKeys: fallback.map((f) => f.key),
      includedUseFeatureKeys: [],
    }
  }

  return {
    features,
    perGenerationFeatureKeys,
    includedUseFeatureKeys,
  }
}

export function planFeaturesCatalogFallback() {
  return PLAN_GENERATION_FEATURES.map((key) => ({
    key,
    labelEn: getPlanFeatureLabel(key, "en"),
    labelAr: getPlanFeatureLabel(key, "ar"),
    costType: "images_per_generation",
  }))
}

export function getAllowedFeaturesCatalog(config) {
  const features = config?.features?.length
    ? config.features
    : planFeaturesCatalogFallback()
  return features.filter(
    (f) => !PLAN_TOP_LEVEL_FEATURE_KEYS.includes(f.key)
  )
}

export function isPerGenerationFeatureKey(key, config) {
  if (!key) return false
  const perGen = config?.perGenerationFeatureKeys
  if (Array.isArray(perGen) && perGen.length > 0) {
    return perGen.includes(key)
  }
  const row = config?.features?.find((f) => f.key === key)
  return row?.costType === "images_per_generation"
}

export function isIncludedUseFeatureKey(key, config) {
  if (!key || !config?.includedUseFeatureKeys?.length) return false
  return config.includedUseFeatureKeys.includes(key)
}

export const METADATA_CATEGORY_KEYS = [
  "feature_access",
  "furniture_tools",
  "ai_image_tools",
  "room_planner",
  "advanced_spaces",
  "upcoming_features",
]

export const METADATA_CATEGORY_LABELS = {
  feature_access: "Feature Access",
  furniture_tools: "Furniture Tools",
  ai_image_tools: "AI Design Tools",
  room_planner: "Room Planner",
  advanced_spaces: "Advanced Spaces",
  upcoming_features: "Upcoming Features",
}

export const METADATA_ARABIC_KEYS = {
  feature_access: "الوصول إلى المزايا",
  furniture_tools: "أدوات الأثاث",
  ai_image_tools: "أدوات تصميم الذكاء الاصطناعي",
  room_planner: "مخطط الغرف",
  advanced_spaces: "المساحات المتقدمة",
  upcoming_features: "المزايا القادمة",
}

export function getMetadataCategoryLabel(category, lang = "en") {
  if (lang === "ar") {
    return METADATA_ARABIC_KEYS[category] || METADATA_CATEGORY_LABELS[category]
  }
  return METADATA_CATEGORY_LABELS[category] || category
}

/**
 * Arabic labels arrive from the API with inconsistent orthography: optional
 * harakat, alef/ya/ta-marbuta variants, tatweel, doubled spaces. Matching raw
 * strings drops whole categories, so compare on a normalized form.
 */
function normalizeArabicText(value) {
  return String(value || "")
    .replace(/[\u064B-\u0652\u0670\u0640]/g, "")
    .replace(/[\u0622\u0623\u0625\u0627]/g, "\u0627")
    .replace(/[\u0649\u064A]/g, "\u064A")
    .replace(/\u0629/g, "\u0647")
    .replace(/\s+/g, " ")
    .trim()
}

/** Arabic labels retired by a rename — still present on already-saved plans. */
const LEGACY_METADATA_ARABIC_KEYS = {
  ai_image_tools: [
    "أدوات الصور بالذكاء الاصطناعي",
    "أدوات التصميم بالذكاء الاصطناعي",
  ],
  advanced_spaces: ["مساحات متطورة", "المساحات المتطورة"],
}

const METADATA_ARABIC_TO_CATEGORY = Object.fromEntries(
  Object.entries(METADATA_ARABIC_KEYS).flatMap(([category, label]) => [
    [label, category],
    [normalizeArabicText(label), category],
    ...(LEGACY_METADATA_ARABIC_KEYS[category] || []).flatMap((legacy) => [
      [legacy, category],
      [normalizeArabicText(legacy), category],
    ]),
  ])
)

/** GET /account/admin/plans/ metadata labels → internal slug */
const API_METADATA_LABEL_TO_SLUG = {
  "feature access": "feature_access",
  "furniture tools": "furniture_tools",
  "ai image tools": "ai_image_tools",
  "upcoming features": "upcoming_features",
  "room planner": "room_planner",
  "advanced spaces": "advanced_spaces",
}

const API_METADATA_ARABIC_LABEL_TO_SLUG = {
  "الوصول إلى المزايا": "feature_access",
  "أدوات الأثاث": "furniture_tools",
  "أدوات الصور بالذكاء الاصطناعي": "ai_image_tools",
  "المزايا القادمة": "upcoming_features",
  "مخطط الغرف": "room_planner",
  "المساحات المتقدمة": "advanced_spaces",
}

export const PLAN_METADATA_SECTION_ORDER = [
  "feature_access",
  "room_planner",
  "furniture_tools",
  "ai_image_tools",
  "advanced_spaces",
  "upcoming_features",
]

/** Maps API metadata keys (snake_case or display labels) → internal category slug */
function resolveMetadataCategoryKey(rawKey) {
  if (!rawKey) return null
  const key = String(rawKey).trim()
  if (METADATA_CATEGORY_KEYS.includes(key)) return key
  if (PLAN_METADATA_SECTION_ORDER.includes(key)) return key

  const snake = key.toLowerCase().replace(/\s+/g, "_")
  if (METADATA_CATEGORY_KEYS.includes(snake)) return snake
  if (PLAN_METADATA_SECTION_ORDER.includes(snake)) return snake

  const labelNorm = key.toLowerCase().replace(/\s+/g, " ").trim()
  if (API_METADATA_LABEL_TO_SLUG[labelNorm]) {
    return API_METADATA_LABEL_TO_SLUG[labelNorm]
  }
  for (const category of METADATA_CATEGORY_KEYS) {
    if (METADATA_CATEGORY_LABELS[category].toLowerCase() === labelNorm) {
      return category
    }
  }

  return (
    METADATA_ARABIC_TO_CATEGORY[key] ||
    API_METADATA_ARABIC_LABEL_TO_SLUG[key] ||
    METADATA_ARABIC_TO_CATEGORY[normalizeArabicText(key)] ||
    null
  )
}

function slugifyMetadataKey(rawKey) {
  return String(rawKey || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
}

/** Display label for a metadata slug (prefers API key text when available). */
function getMetadataSectionLabel(slug, rawMetadata = {}) {
  if (METADATA_CATEGORY_LABELS[slug]) return METADATA_CATEGORY_LABELS[slug]
  for (const rawKey of Object.keys(rawMetadata || {})) {
    if (resolveMetadataCategoryKey(rawKey) === slug) return rawKey
  }
  return slug
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * Normalizes API metadata object to internal snake_case keys.
 * @param {Record<string, string[]>} metadata
 */
export function normalizeMetadataFromApi(metadata) {
  const normalized = buildEmptyMetadata()
  if (!metadata || typeof metadata !== "object") return normalized

  Object.entries(metadata).forEach(([rawKey, values]) => {
    if (!Array.isArray(values)) return
    const items = values
      .map((v) => (typeof v === "string" ? v.trim() : String(v ?? "")))
      .filter(Boolean)
    if (!items.length) return

    const category = resolveMetadataCategoryKey(rawKey)
    const targetKey = category || slugifyMetadataKey(rawKey)
    normalized[targetKey] = [...(normalized[targetKey] || []), ...items]
  })

  return normalized
}

/**
 * @param {Record<string, string[]>} metadataArabic
 */
export function normalizeMetadataArabicFromApi(metadataArabic) {
  const normalized = buildEmptyMetadataArabic()
  if (!metadataArabic || typeof metadataArabic !== "object") return normalized

  Object.entries(metadataArabic).forEach(([rawKey, values]) => {
    if (!Array.isArray(values)) return
    const items = values
      .map((v) => (typeof v === "string" ? v.trim() : String(v ?? "")))
      .filter(Boolean)
    if (!items.length) return

    const category = resolveMetadataCategoryKey(rawKey)
    // An unrecognized Arabic key used to be discarded outright, which silently
    // emptied a whole category; keep it under its own key so nothing is lost.
    const arKey = (category && METADATA_ARABIC_KEYS[category]) || rawKey
    normalized[arKey] = [...(normalized[arKey] || []), ...items]
  })

  return normalized
}

export function getDefaultFeatureImageCosts(config = null) {
  const keys =
    config?.perGenerationFeatureKeys?.length > 0
      ? config.perGenerationFeatureKeys
      : PLAN_GENERATION_FEATURES
  return Object.fromEntries(keys.map((key) => [key, "0"]))
}

export function buildFeatureImageCostsForApi(form, config = null) {
  const costs = {}
  const allowed = form.allowedFeatures || []
  allowed.forEach((key) => {
    if (PLAN_TOP_LEVEL_FEATURE_KEYS.includes(key)) return
    if (config && !isPerGenerationFeatureKey(key, config)) return
    const raw = form.featureImageCosts?.[key]
    const n = parseInt(raw, 10)
    if (!Number.isNaN(n) && n > 0) costs[key] = n
  })
  return costs
}

export function resolveAllowedFeaturesFromForm(form, config = null) {
  let allowed = []
  if (Array.isArray(form.allowedFeatures) && form.allowedFeatures.length > 0) {
    allowed = [...form.allowedFeatures]
  } else {
    allowed = Object.keys(buildFeatureImageCostsForApi(form, config))
  }
  return allowed.filter((key) => !PLAN_TOP_LEVEL_FEATURE_KEYS.includes(key))
}

function featureImageCostsFromPlan(plan = {}) {
  const costs = getDefaultFeatureImageCosts()
  if (
    plan.feature_image_costs &&
    typeof plan.feature_image_costs === "object"
  ) {
    Object.entries(plan.feature_image_costs).forEach(([key, value]) => {
      const n = Number(value)
      if (!Number.isNaN(n) && n > 0) costs[key] = String(n)
    })
    return costs
  }
  PLAN_GENERATION_FEATURES.forEach((key) => {
    const n = Number(plan[key])
    if (!Number.isNaN(n) && n > 0) costs[key] = String(n)
  })
  return costs
}

function allowedFeaturesFromPlan(plan = {}) {
  if (Array.isArray(plan.allowed_features) && plan.allowed_features.length > 0) {
    return [...plan.allowed_features]
  }
  const fromCosts = Object.keys(
    plan.feature_image_costs && typeof plan.feature_image_costs === "object"
      ? plan.feature_image_costs
      : {}
  ).filter((key) => {
    const n = Number(plan.feature_image_costs[key])
    return !Number.isNaN(n) && n > 0
  })
  if (fromCosts.length) return fromCosts
  return PLAN_GENERATION_FEATURES.filter((key) => Number(plan[key]) > 0)
}

export function buildEmptyMetadata() {
  return Object.fromEntries(
    METADATA_CATEGORY_KEYS.map((key) => [key, []])
  )
}

export function buildEmptyMetadataArabic() {
  return Object.fromEntries(
    Object.values(METADATA_ARABIC_KEYS).map((key) => [key, []])
  )
}

export function buildMetadataFromFeatures(features = []) {
  const metadata = buildEmptyMetadata()
  features.forEach((feature) => {
    const label = feature?.label?.trim()
    if (!label || !metadata[feature.category]) return
    metadata[feature.category].push(label)
  })
  return metadata
}

/** Metadata keys as returned by GET /account/admin/plans/ (display labels). */
export function buildMetadataForApi(features = []) {
  const internal = buildMetadataFromFeatures(features)
  return Object.fromEntries(
    METADATA_CATEGORY_KEYS.map((category) => [
      METADATA_CATEGORY_LABELS[category],
      internal[category] || [],
    ])
  )
}

export function buildMetadataArabicForApi(features = []) {
  const metadata_arabic = buildEmptyMetadataArabic()
  features.forEach((feature) => {
    const labelAr = feature?.labelAr?.trim()
    if (!labelAr || !feature?.category) return
    const arKey = METADATA_ARABIC_KEYS[feature.category]
    if (arKey) metadata_arabic[arKey].push(labelAr)
  })
  return metadata_arabic
}

export function featuresFromMetadata(metadata = {}, metadataArabic = {}) {
  const features = []
  let idCounter = 0
  const normalized = normalizeMetadataFromApi(metadata)
  const normalizedAr = normalizeMetadataArabicFromApi(metadataArabic)

  METADATA_CATEGORY_KEYS.forEach((category) => {
    const labels = normalized[category] || []
    const arKey = METADATA_ARABIC_KEYS[category]
    const arLabels = normalizedAr[arKey] || []

    const maxLen = Math.max(labels.length, arLabels.length)
    for (let index = 0; index < maxLen; index += 1) {
      const label = labels[index] || ""
      const labelAr = arLabels[index] || ""
      if (!label.trim() && !labelAr.trim()) continue
      idCounter += 1
      features.push({
        id: `${category}-${idCounter}`,
        category,
        label,
        labelAr,
      })
    }
  })

  return features
}

/** Collapsible feature sections for grid plan cards. */
export function getPlanFeatureSections(plan, lang = "en") {
  const raw = plan?._raw || plan
  const metadataRaw = raw?.metadata || {}
  const metadataArRaw = raw?.metadata_arabic || {}
  const metadata = normalizeMetadataFromApi(metadataRaw)
  const metadataAr = normalizeMetadataArabicFromApi(metadataArRaw)
  const useArabic = lang === "ar"

  const slugOrder = [
    ...PLAN_METADATA_SECTION_ORDER,
    ...Object.keys(metadata).filter(
      (key) => !PLAN_METADATA_SECTION_ORDER.includes(key)
    ),
  ]

  const sections = []
  const seen = new Set()

  for (const slug of slugOrder) {
    if (seen.has(slug)) continue
    const items = metadata[slug] || []
    if (!items.length) continue
    seen.add(slug)

    let label = getMetadataSectionLabel(slug, metadataRaw)
    if (useArabic) {
      // Known categories use our own label, not the API's: a renamed category
      // otherwise keeps showing its retired name for every plan saved before
      // the rename. Unknown slugs still fall back to the API's key text.
      if (METADATA_ARABIC_KEYS[slug]) {
        label = METADATA_ARABIC_KEYS[slug]
      } else {
        for (const arKey of Object.keys(metadataArRaw)) {
          if (resolveMetadataCategoryKey(arKey) === slug) {
            label = arKey
            break
          }
        }
      }
    }

    const arKey = METADATA_ARABIC_KEYS[slug]
    const arItems = arKey ? metadataAr[arKey] || [] : []
    const displayItems = useArabic && arItems.length > 0 ? arItems : items

    sections.push({ key: slug, label, items: displayItems })
  }

  return sections
}

export const PLAN_TYPE_TO_API = {
  "One-Time": "one_time",
  Subscription: "subscription",
}

function planTypeToApi(planType) {
  return PLAN_TYPE_TO_API[planType] || "subscription"
}

function billingCycleToApi(billingCycle) {
  return billingCycle === "Yearly" ? "yearly" : "monthly"
}

const DURATION_TYPE_ONE_TIME = "One Time Plan"

function durationLabelFromForm(form) {
  if (form.planType === "One-Time") return DURATION_TYPE_ONE_TIME
  return form.billingCycle === "Yearly" ? "Yearly" : "Monthly"
}

function durationTypeToApi(form) {
  if (form.planType === "One-Time") return DURATION_TYPE_ONE_TIME
  return form.billingCycle === "Yearly" ? "Yearly" : "Monthly"
}

function durationLabelFromApi(raw) {
  const v = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
  if (
    v === "one_time" ||
    v === "onetime" ||
    v === "one_time_plan" ||
    raw === DURATION_TYPE_ONE_TIME ||
    raw === "One-Time"
  ) {
    return "One-Time"
  }
  if (v === "yearly" || v === "year") return "Yearly"
  if (v === "monthly" || v === "month") return "Monthly"
  if (raw === "Yearly" || raw === "Monthly") return raw
  return "Monthly"
}

function planCategoryToApi(planCategory) {
  const v = String(planCategory || "standard")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")
  if (v === "organization" || v === "organisation" || v === "org") {
    return "organization"
  }
  return "standard"
}

function buildPlanBasePayload(form) {
  const config = form.planFeaturesConfig || null
  const metadata = buildMetadataForApi(form.features)
  const metadata_arabic = buildMetadataArabicForApi(form.features)
  const allowed_features = resolveAllowedFeaturesFromForm(form, config)
  const feature_image_costs = buildFeatureImageCostsForApi(form, config)

  return {
    name: form.planName.trim(),
    name_arabic: (form.planNameArabic || "").trim(),
    description: (form.description || "").trim().slice(0, 100),
    description_arabic: (form.descriptionArabic || "").trim().slice(0, 100),
    type: planTypeToApi(form.planType),
    plan_type: planCategoryToApi(form.planCategory),
    billing_cycle:
      form.planType === "Subscription"
        ? billingCycleToApi(form.billingCycle)
        : "monthly",
    duration_type: durationTypeToApi(form),
    price: String(form.price ?? "0"),
    price_usd: String(form.priceUsd ?? "0"),
    discounted: Boolean(form.discounted),
    discounted_price: form.discounted
      ? String(form.discountedPrice ?? "0")
      : "0",
    discounted_price_usd: form.discounted
      ? String(form.discountedPriceUsd ?? "0")
      : "0",
    images: parseInt(form.images, 10) || 0,
    furniture_finder: parseInt(form.furnitureFinder, 10) || 0,
    sub_name: durationLabelFromForm(form),
    allowed_features,
    feature_image_costs,
    metadata,
    metadata_arabic,
  }
}

/**
 * @param {object} form
 * @param {"draft"|"confirm"} mode
 */
export function buildPlanCreatePayload(form, mode) {
  const isDraft = mode === "draft"
  const isConfirm = mode === "confirm"
  const payload = buildPlanBasePayload(form)

  if (isDraft) {
    payload.save_as_draft = true
    payload.status = "Draft"
    payload.active_status = null
  }

  if (isConfirm) {
    payload.confirm = true
    payload.status = form.activeStatus ? "Active" : "Inactive"
    payload.active_status = Boolean(form.activeStatus)
  }

  return payload
}

/**
 * Update body — omits save_as_draft / confirm per API contract.
 * @param {object} form
 * @param {"draft"|"confirm"} mode
 */
export function buildPlanUpdatePayload(form, mode) {
  const payload = buildPlanBasePayload(form)
  delete payload.sub_name

  if (mode === "draft") {
    payload.status = "Draft"
    payload.active_status = null
  } else if (mode === "confirm") {
    payload.status = form.activeStatus ? "Active" : "Inactive"
    payload.active_status = Boolean(form.activeStatus)
  }

  return payload
}

export function parsePlanFromApiResponse(data) {
  if (!data) return null
  if (data.plan && typeof data.plan === "object") return data.plan
  if (data.result && typeof data.result === "object") return data.result
  if (data.id != null) return data
  return null
}

export function isPlansApiResponseSuccess(response) {
  if (!response) return false
  if (response.status !== 200 && response.status !== 201) return false
  const data = response.data
  if (data?.success === false) return false
  if (data?.error) return false
  return true
}

export function mapPlanDetailToForm(plan) {
  if (!plan) return null

  const planType =
    plan.type === "One-Time" ||
    String(plan.type || "").toLowerCase() === "one-time" ||
    String(plan.type || "").toLowerCase() === "one_time"
      ? "One-Time"
      : "Subscription"

  const durationLabel = durationLabelFromApi(
    plan.duration_type || plan.sub_name || "monthly"
  )
  const billingCycle = durationLabel === "Yearly" ? "Yearly" : "Monthly"

  const metadataArabicRaw = plan.metadata_arabic || {}

  return {
    planName: plan.name || "",
    planNameArabic: plan.name_arabic || "",
    description: plan.description || "",
    descriptionArabic: plan.description_arabic || "",
    planType,
    planCategory: planCategoryToApi(plan.plan_type),
    billingCycle,
    price: plan.price != null ? String(plan.price) : "",
    priceUsd: plan.price_usd != null ? String(plan.price_usd) : "",
    images: String(plan.images ?? plan.image_count ?? 0),
    furnitureFinder: String(plan.furniture_finder ?? 0),
    discounted: Boolean(plan.discounted),
    discountedPrice:
      plan.discounted_price != null ? String(plan.discounted_price) : "",
    discountedPriceUsd:
      plan.discounted_price_usd != null ? String(plan.discounted_price_usd) : "",
    activeStatus: plan.status === "Active",
    activeFeatureTab: "feature_access",
    features: featuresFromMetadata(plan.metadata || {}, metadataArabicRaw),
    allowedFeatures: allowedFeaturesFromPlan(plan).filter(
      (key) => !PLAN_TOP_LEVEL_FEATURE_KEYS.includes(key)
    ),
    featureImageCosts: featureImageCostsFromPlan(plan),
    planFeaturesConfig: null,
    status: plan.status,
    subName: plan.sub_name || durationLabel,
  }
}

/**
 * @returns {string|null} Translation key under createPlan, or null if valid
 */
export function getPlanFormRequiredFieldErrorKey(form) {
  if (!String(form?.planNameArabic ?? "").trim()) {
    return "plan_name_arabic_required"
  }
  if (!String(form?.description ?? "").trim()) {
    return "description_required"
  }
  if (!String(form?.descriptionArabic ?? "").trim()) {
    return "description_arabic_required"
  }
  if (!String(form?.price ?? "").trim()) {
    return "price_required"
  }
  if (!String(form?.priceUsd ?? "").trim()) {
    return "price_usd_required"
  }
  if (form?.discounted) {
    if (!String(form?.discountedPrice ?? "").trim()) {
      return "discounted_price_required"
    }
    if (!String(form?.discountedPriceUsd ?? "").trim()) {
      return "discounted_price_usd_required"
    }
  }
  return null
}

/** Every feature row must have both English and Arabic descriptions. */
export function validatePlanFormFeatures(form) {
  const features = form?.features || []
  return features.some((f) => {
    const en = (f?.label || "").trim()
    const ar = (f?.labelAr || "").trim()
    return !en || !ar
  })
}

const PLANS_API_UNPARSED = "__PLANS_API_UNPARSED__"

export function getPlansApiError(error, lang = "en") {
  const data = error?.response?.data
  if (data && typeof data === "object") {
    const msg = getStoreApiErrorMessage(data, lang, { generic: PLANS_API_UNPARSED })
    if (msg !== PLANS_API_UNPARSED) return msg
  }
  if (!error?.response && typeof error?.message === "string" && error.message.trim()) {
    return error.message.trim()
  }
  return ""
}

export function getPlansApiSuccessMessage(
  data,
  lang,
  fallbackEn,
  fallbackAr
) {
  if (lang === "ar" && data?.messageArabic) return data.messageArabic
  if (data?.message) return data.message
  return lang === "ar" ? fallbackAr : fallbackEn
}
